import React, { useState } from 'react';
import { useNavigate } from 'react-router';

import PlayerList from './PlayerList';
import { draw, objectToBase64 } from '../utils';
import { Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import PickingTeamForm from './PickingTeamForm';
import {
  PickTeamGroup,
  PickTeamPlayer,
  PickTeamTeam,
  PlayerCadenceResult,
} from '../types';
import PlayersCadenceForm from './PlayersCadenceForm';

export default function Home() {
  const navigate = useNavigate();
  const [view, setView] = useState('splitTeams');

  const handleViewChange = (
    _: React.MouseEvent<HTMLElement>,
    newView: string
  ) => {
    setView(newView);
  };

  const handleDraw = (
    playerNames: string[],
    numTeam: number,
    date: string,
    doNotShuffle?: boolean
  ) => {
    const players = playerNames.map((name) => ({ name }));
    const result = draw(players, numTeam, date, doNotShuffle);
    navigate(`/results/${objectToBase64(result)}`);
  };

  const handlePickTeams = ({
    players,
    numberOfWeeks,
    numberOfTeams,
    captains,
    startDate,
  }: {
    players: PickTeamPlayer[];
    numberOfWeeks: number;
    numberOfTeams: number;
    captains: string[];
    startDate: string;
  }) => {
    // split players into groups of appearances
    const groups = players.reduce(
      (acc, player) => {
        if (!acc[player.appearances]) {
          acc[player.appearances] = [];
        }
        acc[player.appearances].push({
          name: player.name,
          appearances: player.appearances,
          goals: player.goals,
          assists: player.assists,
        });
        return acc;
      },
      {} as Record<number, PickTeamPlayer[]>
    );

    const mainGroups: PickTeamGroup[] = Object.keys(groups)
      .filter((appearances: string) => appearances !== '0')
      .sort()
      .reverse()
      .map((appearances: string) => ({
        appearances: parseInt(appearances),
        players: groups[parseInt(appearances)],
      }));

    const teams: PickTeamTeam[] = [];

    // Remove captains from respective teams
    captains.forEach((captain) => {
      let captainPlayer: PickTeamPlayer | undefined;

      mainGroups.forEach((group) => {
        captainPlayer = group.players.find((player) => player.name === captain);
        if (captainPlayer) {
          group.players.splice(group.players.indexOf(captainPlayer), 1);
          teams.push({
            captain,
            players: [
              {
                name: captain,
                appearances: captainPlayer.appearances,
              },
            ],
          });
        }
      });
    });

    let splitGroups: PickTeamGroup[] = [];
    const groupSize = 8;

    // Split the groups so that if a group has more than 10 items, split it by chunks of 10
    mainGroups.forEach((group) => {
      if (group.players.length <= groupSize) {
        splitGroups.push(group);
      } else {
        for (let i = 0; i < group.players.length; i += groupSize) {
          splitGroups.push({
            appearances: group.appearances,
            players: group.players.slice(i, i + groupSize),
          });
        }
      }
    });

    navigate(
      `/pick-teams/${objectToBase64({
        groups: splitGroups,
        numberOfWeeks: numberOfWeeks,
        numberOfTeams: numberOfTeams,
        startDate,
        pickRound: 0,
        pickedCaptains: [],
        teams,
      })}`
    );
  };

  const handleCheckPlayersCadence = (result: PlayerCadenceResult) => {
    navigate(`/players-cadence/${objectToBase64(result)}`);
  };

  return (
    <Stack>
      <Stack
        direction="column"
        sx={{
          width: {
            xs: '100%',
            md: '800px',
          },
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignSelf: 'center',
        }}
      >
        <ToggleButtonGroup
          color="primary"
          value={view}
          exclusive
          onChange={handleViewChange}
          size="small"
          sx={{
            mb: 4,
            justifyContent: 'center',
          }}
        >
          <ToggleButton value="splitTeams">Split teams</ToggleButton>
          <ToggleButton value="pickTeams">Pick teams</ToggleButton>
          <ToggleButton value="checkPlayersCadence">
            Check players cadence
          </ToggleButton>
        </ToggleButtonGroup>
        {view === 'splitTeams' && <PlayerList onDraw={handleDraw} />}
        {view === 'pickTeams' && <PickingTeamForm onDraw={handlePickTeams} />}
        {view === 'checkPlayersCadence' && (
          <PlayersCadenceForm onDraw={handleCheckPlayersCadence} />
        )}
      </Stack>
    </Stack>
  );
}
